import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Table from 'rc-table';

import { formatColumns, getDataChunkForPage, getTypeClass, sortData } from './helpers';
import { GeneralPagination } from 'sharedComponents/GeneralPagination';
import { assetTypes } from 'helpers/constants';

import './style.scss';

const GeneralTableComponent = ({
    className = '',
    scroll,
    columns,
    data,
    emptyText = '',
    tableLayout = 'auto',
    paginationProps,
    tableKey,
    expandable,
    initialSortingProps = { column: null, direction: null },
    type,
    requiredPaginateHandler = false,
    setSelectedEventItemPage,
    selectedEventItemPage,
    selectedEventId,
    isEventFiltered,
    rowClickEvent,
    rowClassName: externalRowClassName
}) => {

    const [sortingColumn, setSortingColumn] = useState(initialSortingProps.column);
    const [sortingDirection, setSortingDirection] = useState(initialSortingProps.direction);

    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedPage, setSelectedPage] = useState(1);

    // Stable sorting handlers to prevent unnecessary re-renders
    const handleSetSortingColumn = useCallback((column) => {
        setSortingColumn(column);
    }, []);

    const handleSetSortingDirection = useCallback((direction) => {
        setSortingDirection(direction);
    }, []);

    useEffect(() => {
        if (requiredPaginateHandler && selectedEventItemPage) {
            setSelectedPage(selectedEventItemPage);
        } else if (Array.isArray(data) && data.length) {
            if (selectedPage !== 1) {
                setSelectedPage(1);
            }
        } else {
            setSelectedPage(0);
        }
    }, [data, requiredPaginateHandler, selectedEventItemPage]);

    const handlePageSelection = (page) => {
        setSelectedPage(page);
        if (requiredPaginateHandler) {
            setSelectedEventItemPage(page); // Update the parent state
        }
    };

    const handleRowClick = (_record, index) => {
        setSelectedRow(index);
        if (requiredPaginateHandler) {
            setSelectedEventItemPage(selectedPage);
        }
        if (rowClickEvent) {
            // Trigger a function When click on the row anywhere.
            rowClickEvent(_record);
        }
    };

    useEffect(() => {
        if (requiredPaginateHandler && selectedEventItemPage) {
            if (isEventFiltered) {
                setSelectedPage(selectedEventItemPage || 1);
            } else if (selectedPage !== selectedEventItemPage) {
                setSelectedPage(selectedEventItemPage);
            }
        }
    }, [selectedEventItemPage, isEventFiltered]);

    // Use useMemo to avoid duplication issues
    const formattedColumns = useMemo(() => {
        return formatColumns(columns, sortingColumn, sortingDirection, handleSetSortingColumn, handleSetSortingDirection);
    }, [columns, sortingColumn, sortingDirection, handleSetSortingColumn, handleSetSortingDirection]);

    const formattedData = useMemo(() => {
        const sortedData = sortData(data, sortingColumn, sortingDirection);

        const dataChunk = paginationProps ? getDataChunkForPage(sortedData, selectedPage, paginationProps.pageSize) : [...sortedData];

        return dataChunk;
    }, [data, sortingColumn, sortingDirection, selectedPage, paginationProps?.pageSize]);

    useEffect(() => {
        if (requiredPaginateHandler && selectedEventId && selectedEventItemPage) {
            const isEventInCurrentPage = formattedData.some((row) => row.eventId === selectedEventId);
            if (isEventInCurrentPage) {
                const selectedRow = document.querySelectorAll('.rc-table-tbody .rc-table-row.selected, .rc-table-tbody .rc-table-row.user-selected')[0];
                if (selectedRow) {
                    selectedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
    }, [selectedEventId, formattedData]);

    return <>
        <Table
            key={`table-page-${selectedPage}-sort-${sortingColumn}-${sortingDirection}`}
            scroll={scroll}
            columns={formattedColumns}
            data={formattedData}
            className={`general-table ${className} ${getTypeClass(type)}`}
            emptyText={!data || !data.length ? emptyText : null}
            tableLayout={tableLayout}
            onRow={(record, index) => ({
                onClick: handleRowClick.bind(null, record, index)
            })}
            rowClassName={(record, index) => {
                if (externalRowClassName) {
                    return externalRowClassName(record, index) || '';
                }
                if (requiredPaginateHandler) {
                    return record.eventId === selectedEventId ? 'selected' : '';
                } else {
                    return selectedRow === index ? 'selected' : '';
                }
            }
            }
            indentSize={30}
            expandable={expandable}
            rowKey={tableKey}
            sticky={false}
            useFixedHeader={false}
        />

        {paginationProps && data && <GeneralPagination
            className='general-table-pagination'
            currentPage={selectedPage}
            total={data?.length}
            pageSize={paginationProps.pageSize ?? 10}
            onChange={handlePageSelection}
            hideOnSinglePage={paginationProps.hideOnSinglePage}
        />}
    </>;
};

GeneralTableComponent.propTypes = {
    className: PropTypes.string,
    columns: PropTypes.any,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    emptyText: PropTypes.string,
    initialSortingProps: PropTypes.shape({
        column: PropTypes.any,
        direction: PropTypes.any
    }),
    paginationProps: PropTypes.shape({
        pageSize: PropTypes.number,
        hideOnSinglePage: PropTypes.bool
    }),
    tableKey: PropTypes.string,
    scroll: PropTypes.any,
    tableLayout: PropTypes.string,
    type: PropTypes.oneOf([assetTypes.PRIMARY, null, undefined]),
    requiredPaginateHandler: PropTypes.bool,
    setSelectedEventItemPage: PropTypes.func,
    selectedEventItemPage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectedEventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isEventFiltered: PropTypes.bool,
    rowClickEvent: PropTypes.func,
    rowClassName: PropTypes.func,
};

export default GeneralTableComponent;
